import { FC, useEffect } from "react";

import { Box, Stack } from "@mui/material";
import { formatCurrencyFromCents } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldError } from "../../hooks/useRateFieldError";
import { useRateFieldName } from "../../hooks/useRateFieldName";
import { AmountField } from "../../RateFields/AmountField/AmountField";

import { isMin, isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { isSweepstakesBaseEntryRateRequired } from "@/features/LiveFormEditor/common/sweepstakes";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

const isEmptyNumericValue = (value: unknown) => value === undefined || value === null || value === "";

export const LotteryEntryFields: FC = () => {
  const { t } = useTranslate();
  const { organization, organizationCurrency } = useCurrentUserContext();
  const { isoLocale } = useLocaleContext();
  const { isSweepstakes } = useRateContext();
  const { control, getValues, setValue } = useFormContext<StepTicketsFormData>();
  const countFieldName = useRateFieldName("attendeesByTicket");
  const amountFieldName = useRateFieldName("amount");
  const isGroupFieldName = useRateFieldName("isGroup");
  const isPayWhatYouCanFieldName = useRateFieldName("isPayWhatYouCan");
  const entryCount = useWatch({ control, name: countFieldName });
  const baseEntryRate = useWatch({ control, name: "sweepstakesBaseEntryRate" });
  const error = useRateFieldError("attendeesByTicket");
  const requiresBaseEntryRate =
    isSweepstakes && isSweepstakesBaseEntryRateRequired(organization?.country, organization?.type);
  const countKeys = {
    sweepstakes: {
      label: "sweepstakes.rates.entryCount.label",
      required: "sweepstakes.rates.entryCount.required",
    },
    raffle: {
      label: "raffle.rates.ticketCount.label",
      required: "raffle.rates.ticketCount.required",
    },
  } as const;
  const countTranslations = countKeys[isSweepstakes ? "sweepstakes" : "raffle"];

  useEffect(() => {
    if (getValues(isGroupFieldName) !== true) {
      setValue(isGroupFieldName, true);
    }
    if (requiresBaseEntryRate && getValues(isPayWhatYouCanFieldName) !== false) {
      setValue(isPayWhatYouCanFieldName, false);
    }
    const amount = Number(entryCount) * Number(baseEntryRate);
    const canComputeAmount =
      !isEmptyNumericValue(entryCount) && !isEmptyNumericValue(baseEntryRate) && Number.isFinite(amount);
    if (requiresBaseEntryRate && canComputeAmount && getValues(amountFieldName) !== amount) {
      setValue(amountFieldName, amount, { shouldDirty: true });
    }
  }, [
    amountFieldName,
    baseEntryRate,
    entryCount,
    getValues,
    isGroupFieldName,
    isPayWhatYouCanFieldName,
    isSweepstakes,
    requiresBaseEntryRate,
    setValue,
  ]);

  return (
    <Stack direction={{ xs: "column", md: "row" }} gap={1.5} alignItems={{ md: "flex-start" }}>
      <Box sx={{ width: { md: 160 }, flexShrink: 0 }}>
        <DashboardTextField
          name={countFieldName}
          label={t("dashboard", countTranslations.label)}
          control={control}
          type="number"
          inputProps={{ min: "1", step: "1" }}
          rules={[
            isRequired(t("dashboard", countTranslations.required)),
            isMin(1, t("dashboard", countTranslations.required)),
            {
              validate: (value: number | string | null | undefined) =>
                Number.isInteger(Number(value)) || t("dashboard", countTranslations.required),
            },
          ]}
          errorMessage={error?.message}
          data-test="sweepstakes-entry-count"
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AmountField
          disabled={requiresBaseEntryRate}
          labelOverride={isSweepstakes ? t("dashboard", "sweepstakes.rates.amount.label") : undefined}
          tooltipText={
            requiresBaseEntryRate
              ? t("dashboard", "sweepstakes.rates.amount.tooltip", {
                  baseEntryRate: formatCurrencyFromCents(Number(baseEntryRate) || 0, {
                    isoLocale,
                    currency: organizationCurrency,
                  }),
                })
              : undefined
          }
        />
      </Box>
    </Stack>
  );
};
